/**
 * Referral program config: PETS reward points, cap, and shareable link templates
 * for both Petso webapp (pet owners) and Petso Pro (pet professionals).
 * Admin sets these in Admin → Rewards → Referrals.
 */

export interface ReferralLinkConfig {
  /** Base URL (e.g. https://app.petso.io). Empty = use current origin in app. */
  baseUrl: string;
  /** Path template; {code} is replaced with referral code (e.g. /join?ref={code}) */
  pathTemplate: string;
}

export interface ReferralConfig {
  /** PETS points when a referred pet owner completes (e.g. first pet) */
  ownerCompletedPoints: number;
  /** PETS points when a referred professional completes */
  professionalCompletedPoints: number;
  /** Maximum number of successful referrals a user can earn rewards from (null = no cap). Not max PETS. */
  capPerReferrer: number | null;
  /** Shareable link for inviting pet owners (Petso webapp signup) */
  webappLink: ReferralLinkConfig;
  /** Shareable link for inviting pet professionals (Petso Pro signup) */
  petsoProLink: ReferralLinkConfig;
}

const DEFAULT_WEBAPP_LINK: ReferralLinkConfig = {
  baseUrl: '',
  pathTemplate: '/join?ref={code}',
};

const DEFAULT_PETSO_PRO_LINK: ReferralLinkConfig = {
  baseUrl: '',
  pathTemplate: '/join?ref={code}',
};

const DEFAULT_CONFIG: ReferralConfig = {
  ownerCompletedPoints: 200,
  professionalCompletedPoints: 300,
  capPerReferrer: null,
  webappLink: { ...DEFAULT_WEBAPP_LINK },
  petsoProLink: { ...DEFAULT_PETSO_PRO_LINK },
};

const STORAGE_KEY = 'petso_referral_config';

function normalizeLinkConfig(
  raw: Partial<ReferralLinkConfig> | undefined,
  defaults: ReferralLinkConfig
): ReferralLinkConfig {
  if (!raw || typeof raw !== 'object') return { ...defaults };
  return {
    baseUrl: typeof raw.baseUrl === 'string' ? raw.baseUrl : defaults.baseUrl,
    pathTemplate: typeof raw.pathTemplate === 'string' ? raw.pathTemplate : defaults.pathTemplate,
  };
}

export function getDefaultReferralConfig(): ReferralConfig {
  return {
    ...DEFAULT_CONFIG,
    webappLink: { ...DEFAULT_WEBAPP_LINK },
    petsoProLink: { ...DEFAULT_PETSO_PRO_LINK },
  };
}

export function loadReferralConfigFromStorage(): ReferralConfig | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const hasLegacy = typeof parsed.baseUrl === 'string' || typeof parsed.pathTemplate === 'string';
    const webappLink = parsed.webappLink && typeof parsed.webappLink === 'object'
      ? normalizeLinkConfig(parsed.webappLink as ReferralLinkConfig, DEFAULT_WEBAPP_LINK)
      : hasLegacy
        ? {
            baseUrl: typeof parsed.baseUrl === 'string' ? parsed.baseUrl : DEFAULT_WEBAPP_LINK.baseUrl,
            pathTemplate: typeof parsed.pathTemplate === 'string' ? parsed.pathTemplate : DEFAULT_WEBAPP_LINK.pathTemplate,
          }
        : { ...DEFAULT_WEBAPP_LINK };
    const petsoProLink = parsed.petsoProLink && typeof parsed.petsoProLink === 'object'
      ? normalizeLinkConfig(parsed.petsoProLink as ReferralLinkConfig, DEFAULT_PETSO_PRO_LINK)
      : { ...DEFAULT_PETSO_PRO_LINK };
    return {
      ownerCompletedPoints:
        typeof parsed.ownerCompletedPoints === 'number' ? parsed.ownerCompletedPoints : DEFAULT_CONFIG.ownerCompletedPoints,
      professionalCompletedPoints:
        typeof parsed.professionalCompletedPoints === 'number'
          ? parsed.professionalCompletedPoints
          : DEFAULT_CONFIG.professionalCompletedPoints,
      capPerReferrer:
        parsed.capPerReferrer === null || (typeof parsed.capPerReferrer === 'number' && parsed.capPerReferrer >= 0)
          ? (parsed.capPerReferrer as number | null) ?? DEFAULT_CONFIG.capPerReferrer
          : DEFAULT_CONFIG.capPerReferrer,
      webappLink,
      petsoProLink,
    };
  } catch {
    return null;
  }
}

export function saveReferralConfigToStorage(config: ReferralConfig): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    // ignore
  }
}

export function getReferralConfig(): ReferralConfig {
  const stored = loadReferralConfigFromStorage();
  return stored ?? getDefaultReferralConfig();
}

function buildLink(linkConfig: ReferralLinkConfig, referralCode: string): string {
  const base = linkConfig.baseUrl.trim() || (typeof window !== 'undefined' ? window.location.origin : '');
  const path = (linkConfig.pathTemplate || '/join?ref={code}').replace(/\{code\}/g, encodeURIComponent(referralCode));
  const pathStart = path.startsWith('/') ? path : `/${path}`;
  return base.replace(/\/$/, '') + pathStart;
}

export function buildReferralLink(referralCode: string): string {
  const config = getReferralConfig();
  return buildLink(config.webappLink, referralCode);
}

export function buildReferralLinkForPetsoPro(referralCode: string): string {
  const config = getReferralConfig();
  return buildLink(config.petsoProLink, referralCode);
}
